import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Eye, EyeOff, AlertTriangle } from "lucide-react";

export default function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "This reset link is missing its token. Request a new email from the Forgot password page.");

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => navigate("/login", { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — redirecting to sign in…");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not reset password. Please request a new link.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <BackgroundLayer section="auth" />
      <div className="flex-1 flex items-center justify-center px-5">
        <div className="glass-card-strong w-full max-w-md p-7 fade-up" data-testid="reset-card">
          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
              >
                <CheckCircle2 size={28} className="text-[#4CAF50]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Password updated</h2>
              <p className="text-soft text-sm">Taking you back to sign in…</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2 text-shadow-green">Choose a new password</h1>
              <p className="text-soft text-sm mb-6">Make sure it's at least 6 characters.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    required
                    data-testid="reset-password-input"
                    className="glass-input pr-11"
                    placeholder="New password"
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!token}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="reset-password-toggle"
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <input
                  type={show ? "text" : "password"}
                  required
                  data-testid="reset-confirm-input"
                  className="glass-input"
                  placeholder="Confirm new password"
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={!token}
                />
                {error && (
                  <div
                    className="flex items-start gap-2 rounded-xl p-3"
                    style={{ background: "rgba(211,47,47,0.12)", border: "1px solid rgba(211,47,47,0.4)" }}
                    data-testid="reset-error"
                  >
                    <AlertTriangle size={14} className="text-[#FF8A8A] mt-0.5 flex-shrink-0" />
                    <p className="text-[12.5px] text-white leading-snug">{error}</p>
                  </div>
                )}
                <button
                  data-testid="reset-submit"
                  disabled={busy || !token}
                  className="btn-primary w-full"
                >
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
          <div className="text-center mt-6 space-x-4">
            <Link to="/login" className="text-[#4CAF50] text-sm hover:underline">← Back to sign in</Link>
            <Link to="/forgot-password" className="text-white/60 text-sm hover:underline">Request new link</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
